import type { CompanyFilter, Employee, OrgState, OrgUnit } from "../types/org";

export const COMPANY_OPTIONS: CompanyFilter[] = ["Qisda", "BenQ", "BQP"];
export const BQP_BUSINESS_OPTIONS = ["BQP", "BQtw"];

export function companyOptionsForState(state: OrgState) {
  const companies = COMPANY_OPTIONS.filter((company) => Object.values(state.units).some((unit) => unitBelongsToCompany(state, unit, company)));
  return companies.length ? companies : COMPANY_OPTIONS;
}

export function unitSourceSystem(state: OrgState, unit?: OrgUnit): string | undefined {
  let current = unit;
  while (current) {
    if (current.sourceSystem) return current.sourceSystem.toLowerCase();
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
  return undefined;
}

export function unitBelongsToCompany(state: OrgState, unit: OrgUnit | undefined, company: CompanyFilter) {
  const source = unitSourceSystem(state, unit);
  if (company === "Qisda") return source === "qisda";
  if (company === "BQP") return source === "benq" && isBqpUnit(state, unit);
  return source === "benq" && !isBqpUnit(state, unit);
}

export function employeeBelongsToCompany(state: OrgState, employee: Employee | undefined, company: CompanyFilter) {
  if (!employee) return false;
  const unit = employee.unitId ? state.units[employee.unitId] : undefined;
  if (unit) return unitBelongsToCompany(state, unit, company);
  const companyCode = String(employee.companyCode ?? "").toLowerCase();
  if (company === "Qisda") return companyCode.includes("qisda");
  if (company === "BQP") return isBqpBusiness(employee.buBg) || companyCode.includes("bqp");
  return companyCode.includes("benq") && !isBqpBusiness(employee.buBg);
}

export function businessOptionsForCompany(state: OrgState, company: CompanyFilter) {
  if (company === "BQP") return BQP_BUSINESS_OPTIONS;
  const values = new Map<string, string>();
  for (const unit of Object.values(state.units)) {
    if (unit.isUnlinked || !unitBelongsToCompany(state, unit, company)) continue;
    addBusinessOption(values, unit.buBg);
  }
  for (const employee of Object.values(state.employees)) {
    if (!employeeBelongsToCompany(state, employee, company)) continue;
    addBusinessOption(values, employee.buBg);
  }
  return [...values.values()].filter((value) => company !== "BenQ" || !isBqpBusiness(value)).sort((a, b) => a.localeCompare(b, "zh-Hant"));
}

export function sourceForCompany(company: CompanyFilter) {
  if (company === "Qisda") return "qisda";
  return "benq";
}

export function bqpRootUnit(state: OrgState) {
  return Object.values(state.units)
    .filter((unit) => unitBelongsToCompany(state, unit, "BQP"))
    .sort((a, b) => a.level - b.level || a.code.localeCompare(b.code) || a.nameZh.localeCompare(b.nameZh))[0];
}

export function isWithinUnitSubtree(state: OrgState, unit: OrgUnit | undefined, rootId: string) {
  let current = unit;
  while (current) {
    if (current.id === rootId) return true;
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
  return false;
}

export function isBqpBusiness(value: unknown) {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "bqp" || normalized === "bqtw";
}

function addBusinessOption(values: Map<string, string>, value: unknown) {
  const normalized = normalizeBusinessOption(value);
  if (!normalized) return;
  values.set(normalized.toLowerCase(), normalized);
}

function normalizeBusinessOption(value: unknown) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  if (trimmed.toLowerCase() === "fin") return "FIN";
  return trimmed;
}

function isBqpUnit(state: OrgState, unit?: OrgUnit): boolean {
  let current = unit;
  while (current) {
    if (isBqpBusiness(current.buBg)) return true;
    const nameText = `${current.nameZh ?? ""} ${current.nameEn ?? ""}`.toLowerCase();
    if (nameText.includes("明基亞太") || nameText.includes("benq asia pacific")) return true;
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
  return false;
}
